/**
 * Rolling_Summarizer — pure predicate (Task 4.2) and async summarize call
 * (Task 4.3).
 *
 * The async {@link summarize} function uses {@link shouldSummarize} as its
 * upstream gate (the caller decides whether to invoke it). Both halves of
 * this module live together because they share the same per-message token
 * accounting heuristic and the same trigger semantics.
 *
 * See design.md → Components and Interfaces → 5. Rolling_Summarizer.
 */

import type { ModelId, RouterResult } from './modelRouter/types';
import type { ConversationThread } from './types/conversation';
import type { LanguageCode } from './types/preferences';

/**
 * Default token estimator (~4 chars per token heuristic).
 *
 * NOTE: This MUST stay in sync with the heuristic exported by
 * `./tokenBudget.ts` (see Task 4.1). The Token_Budgeter and the
 * Rolling_Summarizer have to agree on the cost of a message so that the
 * "unsummarized > 50% of budget" trigger lines up with the windowing
 * decisions made when assembling the prompt history.
 *
 * The 4-chars-per-token approximation is acknowledged in design.md as a
 * deliberate soft estimate; Gemini's tokenizer is server-side, so an exact
 * count is neither available nor required.
 */
const defaultEstimateTokens = (text: string): number => Math.ceil(text.length / 4);

/**
 * Decide whether the rolling summarizer should run for a given thread.
 *
 * Validates Property 6 (design.md → Correctness Properties) — summarization
 * triggers only when the unsummarized history strictly exceeds half of the
 * configured token budget.
 *
 * @param thread - The active Conversation_Thread.
 * @param budget - The configured `tokenBudget` (in token-equivalents).
 * @param estimateTokens - Optional injected estimator, used by tests; defaults
 *   to the same `Math.ceil(length / 4)` heuristic the Token_Budgeter uses.
 * @returns `true` iff the sum of estimated tokens of every message with
 *   `summarized === false` is strictly greater than `budget * 0.5`. Returns
 *   `false` when the budget is non-positive or no unsummarized content
 *   exceeds the threshold.
 *
 * Validates: Requirement 4.6.
 */
export function shouldSummarize(
  thread: ConversationThread,
  budget: number,
  estimateTokens: (text: string) => number = defaultEstimateTokens,
): boolean {
  if (budget <= 0) {
    return false;
  }

  let unsummarizedTokens = 0;
  for (const message of thread.messages) {
    if (!message.summarized) {
      unsummarizedTokens += estimateTokens(message.content);
    }
  }

  return unsummarizedTokens > budget * 0.5;
}

/**
 * Inputs to {@link summarize}. Decoupled from the broader application state
 * so this module stays trivially testable.
 */
export interface SummarizerInput {
  thread: ConversationThread;
  language: LanguageCode;
  /** Configured token budget; informs how aggressively to compress. */
  budgetTokens: number;
}

/**
 * Minimal interface the summarizer needs from the Model_Router executor.
 *
 * Decoupled from the executor's full interface so tests can supply a
 * scripted stub. The real Model_Router executor (Task 9.2) will satisfy
 * this contract via a thin adapter that wraps its main `execute` method
 * around the same fallback / retry policy.
 */
export interface SummarizerRouter {
  /**
   * Send a one-shot prompt; the executor handles fallback and retries
   * internally. The `preferredModel` hint MAY be honored by the executor
   * but it remains free to fall back to any other model in the
   * FALLBACK_CHAIN if the preferred one is in cooldown or otherwise
   * ineligible.
   */
  oneShot(args: {
    systemPrompt: string;
    userText: string;
    preferredModel?: ModelId;
  }): Promise<RouterResult>;
}

/**
 * Result of a single summarization call.
 *
 * - `summary` is `null` exactly when the router returned a `softFallback`.
 *   In that case the caller MUST keep the existing rolling summary intact
 *   and try again on a later turn.
 * - `summarizedThroughMessageId` mirrors `summary`: when a new summary is
 *   produced it is the id of the last message folded into it; when no
 *   summary was produced it is `null`.
 */
export interface SummarizeResult {
  summary: string | null;
  summarizedThroughMessageId: string | null;
}

/**
 * Human-readable display name for a {@link LanguageCode}, used inside the
 * summarization system prompt. The summarization prompt itself is always
 * authored in English (it is an instruction to the model, not user-facing
 * copy); the trailing "Write in {language}" clause is what steers the
 * output language.
 */
function languageDisplayName(language: LanguageCode): string {
  switch (language) {
    case 'tr':
      return 'Turkish';
    case 'en':
      return 'English';
    case 'el':
      return 'Greek';
  }
}

/**
 * Build the fixed summarization system prompt for the requested language.
 *
 * The wording is deliberately stable across calls so summaries from
 * different turns remain stylistically consistent.
 */
function buildSummarizationSystemPrompt(language: LanguageCode): string {
  return (
    `You are a meticulous note-taker. Summarize the following conversation in 6\u201310 sentences. ` +
    `Preserve emotional context, recurring topics, inside jokes, and unresolved questions. ` +
    `Write in ${languageDisplayName(language)}. ` +
    `Do not add new opinions; only summarize what was said.`
  );
}

/**
 * Render the conversation thread as a flat USER/ASSISTANT transcript that
 * is packaged as a single user turn for the one-shot summarization request.
 *
 * Empty-content messages are skipped: they would otherwise produce a bare
 * `USER:` or `ASSISTANT:` label with no content, which the model treats as
 * filler and which can spuriously inflate the prompt size.
 */
function renderTranscript(thread: ConversationThread): string {
  const lines: string[] = [];
  for (const message of thread.messages) {
    if (message.content.length === 0) {
      continue;
    }
    const speaker = message.role === 'user' ? 'USER' : 'ASSISTANT';
    lines.push(`${speaker}: ${message.content}`);
  }
  return lines.join('\n');
}

/**
 * Produce a fresh rolling summary for the given thread by issuing a
 * one-shot request through the Model_Router.
 *
 * The summarization request is biased toward the cheapest available
 * Flash-Lite tier (`gemini-2.5-flash-lite`) per the design — summaries are
 * frequent, short, and don't need flagship reasoning. The router is free
 * to fall back through the chain when the preferred model is in cooldown.
 *
 * The function is async but otherwise pure: no `Date.now`, no random, no
 * network or storage IO beyond the single `router.oneShot` call. This
 * keeps it amenable to property tests with a scripted router stub.
 *
 * Validates Property 6 (design.md) — together with {@link shouldSummarize}
 * the rolling summarizer trigger and execution paths cover the
 * "summarize when unsummarized history exceeds half the budget" invariant.
 *
 * @param input - Conversation thread, language, and configured token
 *   budget. The budget is forwarded to the router so the executor can
 *   decide how aggressively to compress; this module does not truncate
 *   the transcript itself.
 * @param router - A thin {@link SummarizerRouter} adapter over the
 *   Model_Router executor. The actual executor (Task 9.2) will satisfy
 *   this interface via a small wrapper that reuses its fallback and
 *   retry policy.
 * @returns The new rolling summary plus the id of the last message folded
 *   into it on success, or `{ summary: null, summarizedThroughMessageId:
 *   null }` when the router returned a `softFallback`. In the fallback
 *   case the caller MUST keep any existing rolling summary intact.
 *
 * Validates: Requirements 4.5, 4.6.
 */
export async function summarize(
  input: SummarizerInput,
  router: SummarizerRouter,
): Promise<SummarizeResult> {
  const { thread, language } = input;

  const lastMessage = thread.messages[thread.messages.length - 1];
  if (lastMessage === undefined) {
    // Nothing to summarize. Skip the router call entirely; an empty
    // transcript would yield no useful summary.
    return { summary: null, summarizedThroughMessageId: null };
  }

  const systemPrompt = buildSummarizationSystemPrompt(language);
  const userText = renderTranscript(thread);

  const result = await router.oneShot({
    systemPrompt,
    userText,
    preferredModel: 'gemini-2.5-flash-lite',
  });

  if (result.ok) {
    return {
      summary: result.text,
      summarizedThroughMessageId: lastMessage.id,
    };
  }

  // softFallback — keep any existing rolling summary intact upstream.
  return { summary: null, summarizedThroughMessageId: null };
}
